import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, onBeforeMount, ref, watch } from 'vue';
import dateformat from 'dateformat';
import store from '@/store';
import { getCustomer, getInvoices, getPaymentMethod, getSubscriptions } from '@/api/billing';
import type { AxiosError } from 'axios';
import type { Customer, Invoice, PaymentMethod, Subscription, SubscriptionStatus } from '@/api/typings/billing';
import CardIcon from '@/views/Account/components/CardIcon.vue';
import CreditCardInput from '@/views/Account/components/CreditCardInput.vue';
import SubscriptionPauseCancelDialog from '@/views/Account/components/SubscriptionPauseCancelDialog.vue';
import SubscriptionResumeDialog from '@/views/Account/components/SubscriptionResumeDialog.vue';
import CustomilyPlanCard from '@/views/Account/components/CustomilyPlanCard.vue';
import StoreSelector from '@/components/StoreSelector/StoreSelector.vue';
import ShopifyBilling from '@/views/Account/components/ShopifyBilling.vue';
import { StoreType } from '@/typings/user';


export default /*#__PURE__*/_defineComponent({
  __name: 'Billing',
  emits: ['user-updated'],
  setup(__props, { emit }) {



const invoicesPerPage = 10;
const activeStatuses: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];
const invoiceTableFields = [
    { key: 'date', label: 'Date' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount', class: 'text-center' },
    { key: 'status', label: 'Status' },
    { key: 'invoice', label: 'Invoice', class: 'text-center' }
];

const storeUrl = ref('');
const isShopifyNewPlan = ref(false);
const paymentMethod: Ref<PaymentMethod | null> = ref(null);
const subscription: Ref<Subscription | null> = ref(null);
const invoices: Ref<Invoice[]> = ref([]);
const customer: Ref<Customer | null> = ref(null);
const pauseCardToken: Ref<string | undefined> = ref(undefined);

const loadingPaymentMethod = ref(true);
const loadingInvoices = ref(true);
const loadingSubscriptions = ref(true);
const showUpdateCardDialog = ref(false);
const showSubscriptionPauseCancelDialog = ref(false);
const showSubscriptionResumeDialog = ref(false);
const showCardAskDialog = ref(false);

const disablePrevBtn = ref(false);
const disableNextBtn = ref(false);

const cancelPauseDialogAllowCancel = ref(true);

const user = computed(() => store.state.user);

const subscriptions = computed(() => store.state.subscriptions);

const stripePublicKey = computed(() => store.state.stripePublicKey);

const subscriptionPausedOrWillPause = computed(
    () => subscription.value?.Status === 'paused' || subscription.value?.WillPauseAt
);

const invoiceItems = computed(() => {
    return invoices.value.map((i) => ({
        date: formatDateString(new Date(i.Created)),
        amount: `$${(i.Total / 100).toFixed(2)}`,
        status: i.Status,
        invoice: i.InvoicePdf
    }));
});

const hasActiveSubscription = computed(() => {
    if (!subscription.value) return false;
    return activeStatuses.includes(subscription.value.Status);
});

const accountDisableMode = computed(() => {
    if (!subscription.value) return false;

    // Check if the current subscription is fully active, that is, it's currently active and it won't be disabled in
    //  the future at the end of the billing cycle
    const isSubscriptionActive =
        hasActiveSubscription.value && !subscription.value.WillCancelAt && !subscription.value.WillPauseAt;

    const activeSubscriptions = subscriptions.value.filter(
        (s) => activeStatuses.includes(s.Status) && !s.WillCancelAt && !s.WillPauseAt
    );

    // If the current subscription is active and there's only 1 subscription active, then this is the only one,
    //  tell the user pausing/canceling this last store will suspend the entire Customily user.
    return (activeSubscriptions.length === 1 && isSubscriptionActive) || !activeSubscriptions.length;
});

const actionEntityString = computed(() => (accountDisableMode.value ? 'account' : 'store'));

const actionEntityStringCapitalized = computed(
    () => actionEntityString.value.charAt(0).toUpperCase() + actionEntityString.value.slice(1)
);

const userStores = computed(() => user.value?.Stores || []);

watch(storeUrl, () => {
    // If there are no stores matching this URL, select the first one available
    if (!user.value?.Stores.some((s) => (s.HasBilling || s.Type === StoreType.Shopify) && s.Url == storeUrl.value)) {
        const validStores = user.value?.Stores.filter((s) => s.HasBilling || s.Type === StoreType.Shopify);
        if (validStores?.length) {
            storeUrl.value = validStores[0].Url;
            isShopifyNewPlan.value = !validStores[0].HasBilling && validStores[0].Type === StoreType.Shopify;
        }
    }
    reloadData(false);
});

watch(showUpdateCardDialog, async () => {
    if (!showUpdateCardDialog.value) {
        loadingPaymentMethod.value = true;
        emit('user-updated');

        // Reload the subscription and the payment method for it
        await loadSubscriptions();
        await loadPaymentMethod();
    }
});

onBeforeMount(() => {
    if (user.value && user.value.Stores?.length) {
        // Get the initial store from the URL if available
        const url = new URL(location.href);
        const store = url.searchParams.get('store');
        if (store) {
            storeUrl.value = store;
            const currentStore = user.value?.Stores.find((s) => s.Url === store);
            isShopifyNewPlan.value = !currentStore?.HasBilling && currentStore?.Type === StoreType.Shopify;
        } else {
            const filteredStores = user.value.Stores.filter((s) => s.HasBilling || s.Type === StoreType.Shopify);
            storeUrl.value = filteredStores[0]?.Url || '';
            isShopifyNewPlan.value = !filteredStores[0].HasBilling && filteredStores[0].Type === StoreType.Shopify;
        }
        reloadData();
    }
});

const setStoreUrl = (newStoreUrl: string) => {
    if (storeUrl.value !== newStoreUrl) {
        storeUrl.value = newStoreUrl;
        const currentStore = user.value?.Stores.find((s) => s.Url === newStoreUrl);
        isShopifyNewPlan.value = !currentStore?.HasBilling && currentStore?.Type === StoreType.Shopify;
        // Reload all the data for the new store
        reloadData(false);
    }
};

const formatDateString = (date: Date | string): string => {
    return dateformat(date, 'mmm dd, yyyy');
};

const startSubscriptionCancelFlow = () => {
    cancelPauseDialogAllowCancel.value = true;
    showSubscriptionPauseCancelDialog.value = true;
};

const startSubscriptionPauseFlow = (askForCard = false) => {
    // Ask for a credit card before pausing the subscription
    if (askForCard) {
        pauseCardToken.value = undefined;
        showCardAskDialog.value = true;
    } else {
        cancelPauseDialogAllowCancel.value = false;
        showSubscriptionPauseCancelDialog.value = true;
    }
};

const startSubscriptionResumeFlow = () => {
    showSubscriptionResumeDialog.value = true;
};

const onCardForPauseSubmit = (token: string) => {
    // Now that we have the card token, start the subscription pausing
    showCardAskDialog.value = false;
    pauseCardToken.value = token;
    startSubscriptionPauseFlow();
};

const goPrevInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, undefined, invoices.value[0].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disablePrevBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const goNextInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, invoices.value[invoices.value.length - 1].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disableNextBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const editCreditCard = () => {
    showUpdateCardDialog.value = true;
};

const onCardSubmit = () => {
    showUpdateCardDialog.value = false;
};

const onSubscriptionCancel = async () => {
    emit('user-updated');
    pauseCardToken.value = undefined;
    loadingSubscriptions.value = true;
    await loadSubscriptions();
};

const onSubscriptionPaused = async () => {
    emit('user-updated');
    pauseCardToken.value = undefined;
    loadingSubscriptions.value = true;
    await loadSubscriptions();
};

const onSubscriptionResumed = async () => {
    emit('user-updated');
    pauseCardToken.value = undefined;
    loadingSubscriptions.value = true;
    await loadSubscriptions();
};

const loadSubscriptions = async (forceReload = true) => {
    loadingSubscriptions.value = true;
    if (forceReload) {
        const _subscriptions = await getSubscriptions();
        store.commit('subscriptions', _subscriptions);
    }
    subscription.value = subscriptions.value.find((s) => s.Store === storeUrl.value) || null;
    loadingSubscriptions.value = false;
};

const loadPaymentMethod = async () => {
    if (subscription.value && subscription.value.PaymentMethodId) {
        loadingPaymentMethod.value = true;
        paymentMethod.value = await getPaymentMethod(storeUrl.value);
    }
    loadingPaymentMethod.value = false;
};

const reloadData = async (forceReload = true) => {
    loadingPaymentMethod.value = true;
    loadingSubscriptions.value = true;
    loadingInvoices.value = true;

    getCustomer(storeUrl.value)
        .then((data) => {
            customer.value = data;
        })
        .catch((error) => {
            customer.value = null;
            if (error.request.status !== 404) {
                window.rumReportError(error);
            }
        });

    // Load subscriptions
    loadSubscriptions(forceReload)
        .then(() => {
            loadPaymentMethod().catch((error) => {
                if (error.request.status !== 404) {
                    window.rumReportError(error);
                }
                paymentMethod.value = null;
                loadingPaymentMethod.value = false;
            });
        })
        .catch((error) => {
            if (error.request.status !== 404) {
                window.rumReportError(error);
            }
            subscription.value = null;
            paymentMethod.value = null;
            loadingSubscriptions.value = false;
            loadingPaymentMethod.value = false;
        });

    try {
        const data = await getInvoices(storeUrl.value, invoicesPerPage);
        invoices.value = data.Invoices;

        disableNextBtn.value = false;
        disablePrevBtn.value = true;
        if (!data.MoreAvailable) {
            disableNextBtn.value = true;
        }
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.request?.status !== 404) {
            window.rumReportError(axiosError);
        }
        invoices.value = [];
    } finally {
        loadingInvoices.value = false;
    }
};

return { __sfc: true,emit, invoicesPerPage, activeStatuses, invoiceTableFields, storeUrl, isShopifyNewPlan, paymentMethod, subscription, invoices, customer, pauseCardToken, loadingPaymentMethod, loadingInvoices, loadingSubscriptions, showUpdateCardDialog, showSubscriptionPauseCancelDialog, showSubscriptionResumeDialog, showCardAskDialog, disablePrevBtn, disableNextBtn, cancelPauseDialogAllowCancel, user, subscriptions, stripePublicKey, subscriptionPausedOrWillPause, invoiceItems, hasActiveSubscription, accountDisableMode, actionEntityString, actionEntityStringCapitalized, userStores, setStoreUrl, formatDateString, startSubscriptionCancelFlow, startSubscriptionPauseFlow, startSubscriptionResumeFlow, onCardForPauseSubmit, goPrevInvoicesPage, goNextInvoicesPage, editCreditCard, onCardSubmit, onSubscriptionCancel, onSubscriptionPaused, onSubscriptionResumed, loadSubscriptions, loadPaymentMethod, reloadData, CardIcon, CreditCardInput, SubscriptionPauseCancelDialog, SubscriptionResumeDialog, CustomilyPlanCard, StoreSelector, ShopifyBilling }
}

})